import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import type { ManualAdjustment } from '../../utils/useStatementModel';

interface ManualAdjustmentsProps {
  adjustments: ManualAdjustment[];
  onChange: (adjustments: ManualAdjustment[]) => void;
}

/**
 * Manual statement lines (label + signed dollar amount, e.g. "Make-good credit −250").
 * Rendered in the statement at the global share; removable; never persisted.
 */
export function ManualAdjustments({ adjustments, onChange }: ManualAdjustmentsProps) {
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');

  function add() {
    const value = Number(amount);
    if (!label.trim() || !Number.isFinite(value) || value === 0) return;
    onChange([...adjustments, { id: `adj-${Date.now()}`, label: label.trim(), amount: value }]);
    setLabel('');
    setAmount('');
    setAdding(false);
  }

  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Manual adjustments</h3>
      {adjustments.length > 0 && (
        <ul className="mb-2 space-y-1">
          {adjustments.map((a) => (
            <li
              key={a.id}
              className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm"
            >
              <span className="min-w-0 truncate text-gray-700">{a.label}</span>
              <span className="ml-2 flex shrink-0 items-center gap-1.5">
                <span className={`tabular-nums ${a.amount < 0 ? 'text-red-700' : 'text-gray-900'}`}>
                  {a.amount < 0 ? '−' : '+'}${Math.abs(a.amount).toFixed(2)}
                </span>
                <button
                  type="button"
                  onClick={() => onChange(adjustments.filter((x) => x.id !== a.id))}
                  className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  aria-label={`Remove adjustment ${a.label}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {adding ? (
        <div className="space-y-1.5 rounded-lg border border-gray-200 bg-white p-2">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Label (e.g. Make-good credit)"
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-emerald-500 focus:outline-none"
          />
          <input
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Amount (negative = credit)"
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm tabular-nums focus:border-emerald-500 focus:outline-none"
            onKeyDown={(e) => e.key === 'Enter' && add()}
          />
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={add}
              className="rounded bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="rounded px-2.5 py-1 text-xs text-gray-500 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex items-center gap-1 rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-sm text-gray-500 hover:border-gray-400 hover:text-gray-700"
        >
          <Plus className="h-3.5 w-3.5" /> Add adjustment
        </button>
      )}
    </div>
  );
}
